import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button } from '@mui/joy';
import { Container } from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useAuth } from '../../main/context/AuthContext';
import { useSiteSettings } from '../../main/context/SiteSettingsContext';
import { getSiteDisplayName } from '../../main/utils/siteDisplayName';

const POLL_MS = 60000;

export default function CustomerPendingApprovalScreen() {
  const { t, i18n } = useTranslation('ui');
  const { logout, refreshUserFromVerify } = useAuth();
  const { settings } = useSiteSettings();
  const siteName = getSiteDisplayName(settings, i18n.language);
  const siteIcon = settings?.site_icon && String(settings.site_icon).trim();
  const [checking, setChecking] = useState(false);
  const pollRef = useRef(null);

  const checkNow = useCallback(async () => {
    setChecking(true);
    try {
      const activated = await refreshUserFromVerify();
      if (activated) {
        window.location.reload();
      }
    } finally {
      setChecking(false);
    }
  }, [refreshUserFromVerify]);

  useEffect(() => {
    pollRef.current = window.setInterval(() => {
      refreshUserFromVerify().then((ok) => {
        if (ok) window.location.reload();
      });
    }, POLL_MS);
    return () => {
      if (pollRef.current != null) window.clearInterval(pollRef.current);
    };
  }, [refreshUserFromVerify]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        refreshUserFromVerify().then((ok) => {
          if (ok) window.location.reload();
        });
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [refreshUserFromVerify]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#fafafa',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Box
          sx={{
            textAlign: 'center',
            p: { xs: 3, sm: 4 },
            borderRadius: '16px',
            backgroundColor: '#fff',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          }}
        >
          {siteIcon ? (
            <Box sx={{ mb: 2 }}>
              <Box
                component="img"
                src={siteIcon}
                alt={siteName}
                sx={{
                  display: 'block',
                  mx: 'auto',
                  mb: 1,
                  maxWidth: 140,
                  maxHeight: 72,
                  objectFit: 'contain',
                }}
              />
              {siteName ? (
                <Typography level="title-md" sx={{ fontWeight: 700, color: '#111827' }}>
                  {siteName}
                </Typography>
              ) : null}
            </Box>
          ) : null}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <HourglassEmptyIcon sx={{ fontSize: 48, color: 'primary.500' }} />
          </Box>
          <Typography level="h3" sx={{ mb: 2 }}>
            {t('customer.pendingApprovalTitle')}
          </Typography>
          {!siteIcon && siteName ? (
            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
              {siteName}
            </Typography>
          ) : null}
          <Typography level="body-md" color="neutral" sx={{ mb: 3 }}>
            {t('customer.pendingApprovalBody')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'stretch' }}>
            <Button
              variant="solid"
              color="primary"
              onClick={checkNow}
              disabled={checking}
              loading={checking}
            >
              {checking ? t('customer.pendingApprovalChecking') : t('customer.pendingApprovalCheckAgain')}
            </Button>
            <Button variant="outlined" color="neutral" onClick={() => logout()}>
              {t('actions.logout')}
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
